// @tg-seq: 10756

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { getTsxFileName } from '../../utils/componentIdentity';
import { Container, Alert, AlertTitle, Button, Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface Props {
    children?: ReactNode;
    location?: any; // React Router location for reset on navigation
}

interface State {
    hasError: boolean;
    error?: Error;
    errorPath?: string; // Track which path caused error
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({ errorPath: window.location.pathname });

        // No self-heal reload here by design: resolve or fail, right here.
        // The fallback UI below (Try Again / Go Home) is the recovery path,
        // user-initiated only - no automatic redirect anywhere in the platform.
    }

    componentDidUpdate(prevProps: Props) {
        // Reset error state when location changes (navigation happens)
        if (this.state.hasError && this.props.location?.pathname !== this.state.errorPath) {
            this.setState({ hasError: false, error: undefined, errorPath: undefined });
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorPath: undefined });
    };

    handleGoHome = () => {
        this.setState({ hasError: false, error: undefined, errorPath: undefined });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <Container sx={{ mt: 5 }}>
                    <Alert severity="error">
                        <AlertTitle>Oops! Something went wrong</AlertTitle>
                        <p>
                            We apologize for the inconvenience. The page you're trying to access
                            encountered an error.
                        </p>
                        <hr />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button variant="outlined" color="error" onClick={this.handleReset}>
                                Try Again
                            </Button>
                            <Button variant="contained" color="primary" onClick={this.handleGoHome}>
                                Go Home
                            </Button>
                        </Box>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details>
                                <summary>Error Details (Development Mode)</summary>
                                <Box sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
                                    {this.state.error.toString()}
                                    <br />
                                    {this.state.error.stack}
                                </Box>
                            </details>
                        )}
                    </Alert>
                </Container>
            );
        }

        return this.props.children;
    }
}

// Wrapper component to inject React Router location
const Self: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    return <ErrorBoundary location={location}>{children}</ErrorBoundary>;
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
